import { formatCalendarDate } from '../../utils/UtilFunctions';

/** Forma mínima que necesitamos de un ciclo previo del alumno. */
export interface PreviousCycleWeek {
  weekNumber?: number;
  trainingSessions?: Array<{ workoutInstances?: unknown[] | null }> | null;
}

export interface PreviousCycle {
  id: number;
  name?: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  trainingWeeks?: PreviousCycleWeek[] | null;
}

export interface WeekOption {
  /** Índice 0-based que espera el backend en `sourceWeekIndex`. */
  index: number;
  weekNumber: number;
  /** Días de esa semana que tienen entrenamiento asignado. */
  sessionsWithWork: number;
  /** True si es la última semana del ciclo. */
  isLast: boolean;
  /**
   * True si es la semana que el wizard preselecciona, o sea la que lleva la ★.
   *
   * #23 ítem 1: antes la estrella se pintaba con `isLast` mientras el
   * deshabilitado miraba `sessionsWithWork`, así que una última semana vacía
   * salía recomendada Y muerta al mismo tiempo. Ahora la marca sale del mismo
   * criterio que la preselección, y nunca cae en una semana no seleccionable.
   */
  isRecommended: boolean;
}

/**
 * Arma las opciones de semana de un ciclo previo, ordenadas por weekNumber.
 *
 * #23: se muestran TODAS las semanas y no solo la última, porque el coach puede
 * querer volver a una anterior — por ejemplo si la última fue de descarga y no
 * sirve como punto de partida.
 */
export function buildWeekOptions(cycle: PreviousCycle | null | undefined): WeekOption[] {
  const weeks = [...(cycle?.trainingWeeks ?? [])].sort((a, b) => (a.weekNumber ?? 0) - (b.weekNumber ?? 0));

  return weeks.map((week, index) => ({
    index,
    weekNumber: week.weekNumber ?? index + 1,
    sessionsWithWork: (week.trainingSessions ?? []).filter((s) => (s.workoutInstances?.length ?? 0) > 0).length,
    isLast: index === weeks.length - 1,
    isRecommended: false
  }));
}

/**
 * Marca con `isRecommended` la semana que el wizard preselecciona.
 *
 * Se apoya en `defaultWeekIndex` para que la ★ y la preselección no puedan
 * separarse nunca. La única diferencia: si NINGUNA semana tiene carga no se
 * marca ninguna, porque marcar una semana deshabilitada es justo el bug que
 * reportó Enzo.
 */
export function markRecommendedWeek(options: WeekOption[]): WeekOption[] {
  const target = defaultWeekIndex(options);
  return options.map((o) => ({
    ...o,
    isRecommended: o.index === target && o.sessionsWithWork > 0
  }));
}

/**
 * Etiqueta del ciclo en el selector: "Nombre · inicio a fin" (#23 ítem 2).
 *
 * Las fechas se leen como día-calendario porque vienen a medianoche UTC; con el
 * Date crudo se mostrarían un día antes desde ART.
 */
export function buildCycleLabel(cycle: PreviousCycle, locale: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'numeric' };
  const start = formatCalendarDate(cycle.startDate, locale, opts);
  const end = formatCalendarDate(cycle.endDate, locale, opts);
  const name = cycle.name?.trim() ?? '';
  const range = start && end ? `${start} a ${end}` : '';

  if (name && range) return `${name} · ${range}`;
  return name || range;
}

/**
 * Semana preseleccionada: la ÚLTIMA (decisión de Luciano).
 *
 * Es donde quedó la carga más alta de la progresión, así que suele ser el punto
 * de partida del ciclo nuevo. Si la última quedó vacía —el coach armó el ciclo
 * pero no llegó a cargarla— se cae a la última que sí tenga entrenamientos,
 * porque arrancar desde una semana vacía no copia nada.
 */
export function defaultWeekIndex(options: WeekOption[]): number | null {
  const last = options[options.length - 1];
  if (!last) return null;

  if (last.sessionsWithWork > 0) return last.index;

  const lastWithWork = [...options].reverse().find((o) => o.sessionsWithWork > 0);
  return lastWithWork ? lastWithWork.index : last.index;
}

/** Ciclos utilizables como origen: los que tienen al menos una semana cargada. */
export function selectableCycles(cycles: PreviousCycle[] | null | undefined): PreviousCycle[] {
  return (cycles ?? []).filter((c) =>
    (c.trainingWeeks ?? []).some((w) => (w.trainingSessions ?? []).some((s) => (s.workoutInstances?.length ?? 0) > 0))
  );
}
